import {
    Children,
    HTMLAttributes,
    PropsWithChildren,
    ReactElement,
    ReactNode,
    cloneElement,
    isValidElement,
} from 'react';

interface IProps extends PropsWithChildren, HTMLAttributes<HTMLElement> {}

export const ChildrenDecorator = ({
    children,
    className = '',
    ...rest
}: IProps) => {
    if (children === null) {
        return null;
    }

    className = className.trim();

    return (
        <>
            {Children.map(children, child =>
                addPropsToNode(child, className, rest),
            )}
        </>
    );
};

export function addPropsToNode(
    node: ReactNode,
    className: string,
    otherProps: Omit<HTMLAttributes<HTMLElement>, 'className'>,
): ReactNode {
    if (node === null || node === undefined) {
        return node;
    }

    if (typeof node !== 'object') {
        return <>{node}</>;
    }

    if (isValidElement(node)) {
        // Explicitly type `node` as ReactElement<any> to avoid `unknown` issues
        const typedNode = node as ReactElement<any>;

        const existingClassName = typedNode.props.className;
        const updatedClassName =
            typeof existingClassName === 'string'
                ? `${existingClassName} ${className}`
                : className;

        return cloneElement(typedNode, {
            className: updatedClassName,
            ...otherProps,
        });
    }

    if (node instanceof Promise) {
        return <>{node.then(n => addPropsToNode(n, className, otherProps))}</>;
    }

    return <>{node}</>;
}
